import { Link, notFound, useParams, useSearch } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { quoteQuery } from '@/queries';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';

import { GarmentMockup } from '@/components/garment-mockup';
import { QuoteStatusBadge, formatDate } from '@/components/status-timeline';
import { ButtonLink, Callout } from '@/components/ui';
import { formatCents, formatCentsOrNone } from '@/lib/money';
import { locationLabel } from '@/lib/pricing/engine';
import { QUOTE_STATUS_LABEL, type QuoteStatus } from '@/lib/types';

/** The customer-facing view of AC-3.5's pipeline. */
const CUSTOMER_STAGES: QuoteStatus[] = [
  'submitted',
  'priced',
  'proof_sent',
  'approved',
  'in_production',
  'shipped',
  'completed',
];

export default function QuoteDetailPage() {
  const { ref } = useParams({ strict: false }) as { ref: string };
  // The token from the link. Without it this page is a 404 for the
  // customer who has never signed in, which is most of them (R-155).
  const { t: token } = useSearch({ strict: false }) as { t?: string };
  const { quote, product } = useSuspenseQuery(quoteQuery(ref, token)).data;
  if (!quote) throw notFound();
  const pendingProof = quote.proofs.find((p) => p.status === 'pending');
  const reached = CUSTOMER_STAGES.indexOf(quote.status);

  return (
    <div className="mx-auto max-w-[1180px] px-[30px] py-8">
      <Link
        to="/orders"
        className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft size={16} weight="bold" aria-hidden />
        Orders and quotes
      </Link>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-sm font-bold text-brand-ink">{quote.ref}</p>
          <h1 className="mt-1 text-[32px] font-semibold tracking-[-0.02em]">
            Quote {quote.ref}
          </h1>
          <p className="mt-1 text-[15px] text-muted">
            Submitted {formatDate(quote.created_at)}
          </p>
        </div>
        <QuoteStatusBadge status={quote.status} />
      </div>

      {pendingProof && (
        <div className="mt-6">
          <Callout tone="brand" title={`Proof round ${pendingProof.round} is waiting on you`}>
            <div className="mt-2">
              <ButtonLink href={`/quotes/${quote.ref}/proof`} size="sm">
                Review the proof
                <ArrowRight size={15} weight="bold" aria-hidden />
              </ButtonLink>
            </div>
          </Callout>
        </div>
      )}

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="space-y-10">
          <section aria-labelledby="progress-heading">
            <h2 id="progress-heading" className="mb-5 text-lg font-bold tracking-[-0.02em]">
              Progress
            </h2>
            {quote.status === 'declined' || quote.status === 'cancelled' ? (
              <Callout
                tone={quote.status === 'declined' ? 'danger' : 'neutral'}
                title={`This quote was ${QUOTE_STATUS_LABEL[quote.status].toLowerCase()}`}
              >
                Get in touch if you would like us to look at it again.
              </Callout>
            ) : (
              <ol className="space-y-4">
                {CUSTOMER_STAGES.map((stage, i) => {
                  const done = reached >= 0 && i <= reached;
                  const current = i === reached;
                  return (
                    <li key={stage} className="flex items-center gap-3">
                      <span
                        aria-hidden
                        className={
                          'grid size-[18px] shrink-0 place-items-center rounded-full border-2 text-[10px] font-bold ' +
                          (current
                            ? 'border-brand bg-brand text-on-brand'
                            : done
                              ? 'border-ok bg-ok text-white'
                              : 'border-line-strong bg-panel')
                        }
                      >
                        {done && !current ? '✓' : ''}
                      </span>
                      <span
                        className={
                          current
                            ? 'text-[15px] font-bold'
                            : done
                              ? 'text-[15px]'
                              : 'text-[15px] text-muted'
                        }
                      >
                        {QUOTE_STATUS_LABEL[stage]}
                        {current && <span className="sr-only"> (current stage)</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          {quote.proofs.length > 0 && (
            <section aria-labelledby="proofs-heading">
              <h2 id="proofs-heading" className="mb-5 text-lg font-bold tracking-[-0.02em]">
                Artwork proofs
              </h2>
              <ul className="space-y-4">
                {quote.proofs.map((proof) => (
                  <li
                    key={proof.id}
                    className="rounded-[10px] border border-line bg-panel p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-bold">Round {proof.round}</p>
                      <span className="text-sm text-muted">
                        {formatDate(proof.created_at)}
                      </span>
                    </div>
                    {proof.note && <p className="mt-2 text-[15px]">{proof.note}</p>}
                    {proof.response_note && (
                      <p className="mt-2 text-[15px] text-muted">
                        Your note: {proof.response_note}
                      </p>
                    )}
                    <p className="mt-3 text-sm font-semibold">
                      {proof.status === 'pending'
                        ? 'Waiting on your response'
                        : proof.status === 'approved'
                          ? 'Approved'
                          : 'Changes requested'}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          {product && (
            <div className="rounded-[10px] border border-line bg-panel p-5">
              <h2 className="text-sm font-bold">Your configuration</h2>
              <div className="checkerboard mt-4 rounded-[10px] border border-line">
                <GarmentMockup
                  type={product.type}
                  colorHex={
                    product.colors.find((c) => c.name === quote.color)?.hex ??
                    product.colors[0]!.hex
                  }
                  designUrl={quote.design_url}
                  placement={{
                    pos_x: quote.pos_x ?? 50,
                    pos_y: quote.pos_y ?? 48,
                    scale: quote.scale ?? 60,
                  }}
                  label={`${product.name} in ${quote.color ?? ''}`}
                  className="aspect-square w-full"
                />
              </div>
              <dl className="mt-4 space-y-2 text-[15px]">
                {[
                  ['Garment', product.name],
                  ['Colour', quote.color ?? ''],
                  ['Size', quote.size ?? ''],
                  ['Locations', quote.locations.map(locationLabel).join(', ')],
                  ['Quantity', String(quote.qty)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-muted">{k}</dt>
                    <dd className="text-right font-semibold">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="rounded-[10px] border border-line bg-panel p-5">
            <h2 className="text-sm font-bold">Pricing</h2>
            {quote.total_cents == null ? (
              <>
                {/* Constitution §14: absent is not zero. `est_total_cents ?? 0`
                    showed a customer **$0.00** beside a timeline reading
                    "Priced" and "Approved", which is the price of the job as
                    far as they can tell. The one real quote in the client's
                    database is exactly that: approved, and every money column
                    null. R-313, and R-284 is the same defect on the staff
                    screen, fixed hours earlier without sweeping for this one. */}
                {quote.est_total_cents == null ? (
                  <p className="mt-4 text-sm text-muted">
                    We have not put a price on this yet. The studio will send one before
                    anything is printed.
                  </p>
                ) : (
                  <>
                    <p className="mt-4 text-2xl font-extrabold">
                      {formatCents(quote.est_total_cents)}
                    </p>
                    <p className="mt-1 text-sm text-muted">
                      Estimate. We confirm the price before anything is printed.
                    </p>
                  </>
                )}
              </>
            ) : (
              <dl className="mt-4 space-y-2.5 text-[15px]">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted">Unit price</dt>
                  <dd className="font-semibold">
                    {formatCentsOrNone(quote.unit_price_cents)}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted">Setup</dt>
                  <dd className="font-semibold">{formatCents(quote.setup_cents ?? 0)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-line pt-3 text-lg">
                  <dt className="font-bold">Total</dt>
                  <dd className="font-extrabold">{formatCents(quote.total_cents)}</dd>
                </div>
                {quote.lead_time && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[13px] text-muted">Lead time</dt>
                    <dd className="text-[13px] text-muted">{quote.lead_time}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
